/**
 * Google Custom Search Ads Module (rampjs) — unified CSA + RSoC.
 *
 * Self-registering tracking module that drives BOTH Google ad types the API
 * can return, dispatching on which `page_options` field the response populates:
 *
 *   - `page_options.rsoc` → RSoC ("Related Search on Content"), the path
 *     ramp.js has always run on publisher pages. Calls
 *     `_googCsa("relatedsearch", ...)` via the google-rsoc machinery. See rsoc.ts.
 *   - `page_options.csa`  → CSA ("Custom Search Ads"), the path served on
 *     renderer SSR/SERP pages. Calls `_googCsa("ads", ...)`. See csa.ts.
 *
 * The API response dictates execution: either, both, or neither handler runs.
 * Supporting both lets one client bundle serve publisher and SSR surfaces
 * without branching on data origin (see ABE-680 Phase C).
 *
 * Consent: the module is consent-exempt. Google CSA/RSoC is keyword-based and
 * exempt from consent gating, so it always runs in full/personalized mode on
 * every surface — matching the previous ramp.js behaviour. (This deliberately
 * drops serp's `personalizedAds=false`-when-no-consent nuance; see the ABE-680
 * decision log.)
 *
 * Order preserved from the previous module: the RSoC handler runs its
 * initAdCallbackTracking → onResponse sequence; when both ad types are present
 * RSoC is initialized before CSA.
 */

import type { Response } from "@ascent/papi-core";
import { registerTrackingPartner } from "../../tracking-registry";
import type { TrackingModule } from "../tracking-module";
import { hasCsa, initializeCsa } from "./csa";
import { hasRsoc, initializeRsoc } from "./rsoc";

export const GoogleCSAModule: TrackingModule = {
  id: "google_csa",
  name: "Google Custom Search Ads",

  // Preserves previous ramp.js behaviour: runs regardless of consent state.
  consentExempt: true,
  requiresExplicitConsent: false,

  /**
   * Enabled whenever the response carries either ad type's page options. The
   * RSoC handler additionally injects the CSA <script> unconditionally inside
   * google-rsoc.onResponse, so when `page_options.rsoc` is present the module
   * must run even if no units are returned.
   */
  isEnabled: (response: Response): boolean =>
    hasRsoc(response) || hasCsa(response),

  initialize: (response: Response): void => {
    // RSoC first (preserves the previous single-handler ordering); CSA second.
    if (hasRsoc(response)) {
      initializeRsoc(response);
    }
    if (hasCsa(response)) {
      initializeCsa(response);
    }
  },
};

registerTrackingPartner(GoogleCSAModule);

export { GoogleCSAModule as default };
